import {environment} from '../../environments/environment';
import {AppConfig, AppDesign, ElementDesign, GeneralState} from '../interfaces/biozoom-api/app-configuration';
import {fillString, fillURL, getKeyVal} from './helpers';
import {fillConfig} from './templateUtils';
import {getMappedColor} from './color-mapping';

const _ = require('lodash');

/**
 * position slider elements within their restrictive box, according to given values
 * @param width width of the slider box (string with unit)
 * @param height height of the slider box (string with unit)
 * @param imageScale factor the background image has to be scaled down by
 * @param screenWidth width of the screen in pixel
 * @param screenHeight height of the screen in pixel
 * @param e element config object, containing information about the slider's dimensions
 * @param localState value store containing all values stored by the app
 */
const getSlider = (
  width: string,
  height: string,
  imageScale: number,
  screenWidth: number,
  screenHeight: number,
  e: ElementDesign,
  localState?: GeneralState
) => {
  if (!width || !height) {
    return;
  }

  // get the rendered dimensions of the box-element in pixel
  const parentWidthRaw = width;
  const parentHeightRaw = height;
  let parentWidth = 0;
  let parentHeight = 0;
  if (parentWidthRaw.includes('%')) {
    parentWidth = Number(parentWidthRaw.replace('%', '')) / 100 * screenWidth;
  } else if (parentWidthRaw.includes('px')) {
    parentWidth = Number(parentWidthRaw.replace('px', ''));
  }
  if (parentHeightRaw.includes('%')) {
    parentHeight = Number(parentHeightRaw.replace('%', '')) / 100 * screenHeight;
  } else if (parentHeightRaw.includes('px')) {
    parentHeight = Number(parentHeightRaw.replace('px', ''));
  }

  // get the height and width of the slider
  let elWidth = e.innerSize?.width ?? '0%';
  let elHeight = e.innerSize?.height ?? '0%';
  let elWidthRaw = 0;
  let elHeightRaw = 0;
  if (elWidth.includes('px')) {
    elWidthRaw = Number(elWidth.replace('px', '')) * imageScale;
    elWidth = `${elWidthRaw}px`;
  } else if (elWidth.includes('%')) {
    elWidthRaw = Number(elWidth.replace('%', '')) / 100 * parentWidth;
    elWidth = `${elWidthRaw}px`;
  }
  if (elHeight.includes('px')) {
    elHeightRaw = Number(elHeight.replace('px', '')) * imageScale;
    elHeight = `${elHeightRaw}px`;
  } else if (elHeight.includes('%')) {
    elHeightRaw = Number(elHeight.replace('%', '')) / 100 * parentHeight;
    elHeight = `${elHeightRaw}px`;
  }
  if (elWidth === 'auto') {
    elWidth = elHeight;
  } else if (elHeight === 'auto') {
    elHeight = elWidth;
  }

  // calculate the x- and y-offset of the slider
  // this is done by taking the dimensions of the box, subtraction the dimensions of the slider,
  // and then using the remaining distance as the minimum- and maximum offset
  let x = '0';
  let y = '0';
  if (e.innerSize.x_key) {
    const min = e.innerSize.x_min ?? 0;
    const max = e.innerSize.x_max ?? 100;
    const val = Number(getKeyVal(`${e.innerSize.x_keyGroup}.${e.innerSize.x_key}`, localState) ?? 0);
    if (max > min) {
      const maxOffset = parentWidth - elWidthRaw;
      const clampedVal = Math.min(Math.max(val, min), max);
      x = `${((clampedVal - min) / (max - min)) * maxOffset}px`;
    }
  }
  if (e.innerSize.y_key) {
    const min = e.innerSize.y_min ?? 0;
    const max = e.innerSize.y_max ?? 100;
    const val = Number(getKeyVal(`${e.innerSize.y_keyGroup}.${e.innerSize.y_key}`, localState) ?? 0);
    if (max > min) {
      const maxOffset = parentHeight - elHeightRaw;
      const clampedVal = Math.min(Math.max(val, min), max);
      y = `${((clampedVal - min) / (max - min)) * maxOffset}px`;
    }
  }

  const zIndex = Number(e.css['z-index'] ?? 10);
  // make the slider box empty, and transfer all css tags to the slider itself
  e.cssInner = {
    ...e.css,
    width: elWidth,
    height: elHeight,
    left: x ?? '0',
    top: y ?? '0',
    position: 'absolute',
    'z-index': `${zIndex + 1}`,
  };
  e.css = {
    width: e.css?.width,
    height: e.css?.height,
    left: e.css?.left,
    top: e.css?.top,
    position: e.css?.position,
    'z-index': `${zIndex}`,
  };
};

/**
 * recalculate an element's dimensions and position
 * @param imageWidth width of the page's canvas
 * @param imageHeight height of the page's canvas
 * @param screenWidth width of the device's screen
 * @param screenHeight height of the device's screen
 * @param css object containing all css tags for the element
 * @param oldWidthPx base width of the element
 * @param oldHeightPx base height of the element
 * @param oldLeftPx base x-offset of the element
 * @param oldTopPx base y-offset of the element
 */
const fixPositionPx = (
  imageWidth: number,
  imageHeight: number,
  screenWidth: number,
  screenHeight: number,
  css: {[key: string]: string},
  oldWidthPx?: string,
  oldHeightPx?: string,
  oldLeftPx?: string,
  oldTopPx?: string,

) => {
  const returnObj = {
    width: oldWidthPx,
    height: oldHeightPx,
    left: oldLeftPx,
    top: oldTopPx,
    imageScale: 1
  };
  if (!imageWidth || !imageHeight) {
    return returnObj;
  }

  // get the element's dimensions in pixel, if possible
  const oldWidth = oldWidthPx !== undefined ? Number((oldWidthPx ?? '').replace('px', '')) : undefined;
  const oldHeight = oldHeightPx !== undefined ? Number((oldHeightPx ?? '').replace('px', '')) : undefined;
  const oldLeft = oldLeftPx !== undefined ? Number((oldLeftPx ?? '').replace('px', '')) : undefined;
  const oldTop = oldTopPx !== undefined ? Number((oldTopPx ?? '').replace('px', '')) : undefined;

  // width:height ratio of the canvas, as well as of the screen
  const imageRatio = imageWidth / imageHeight;
  const screenRatio = screenWidth / screenHeight;

  // scale by which the canvas has to be shrunken down in order to fully fit on the screen
  const imageScale = screenRatio > imageRatio ? screenHeight / imageHeight : screenWidth / imageWidth;
  // x- and y-offset if the canvas fits the screen perfectly
  const oldX = oldLeft * imageScale;
  const oldY = oldTop * imageScale;
  // offsets accounting for the gaps around the canvas
  const deltaW = (screenWidth - imageWidth * imageScale) / 2;
  const deltaH = (screenHeight - imageHeight * imageScale) / 2;
  returnObj.width = !isNaN(oldWidth) ? `${oldWidth * imageScale}px` : undefined;
  returnObj.left = !isNaN(oldLeft) ? `${oldX + deltaW}px` : undefined;
  returnObj.height = !isNaN(oldHeight) ? `${oldHeight * imageScale}px` : undefined;
  returnObj.top = !isNaN(oldTop) ? `${oldY + deltaH}px` : undefined;
  returnObj.imageScale = imageScale;

  // for all additional keys in the css, if they include 'px', scale the preceding number
  for (const [key, val] of Object.entries(css)) {
    if (val.replace(/ /g, '').slice(-2).includes('px')) {
      const valSplit = val.split(' ');
      for (const i of valSplit.keys()) {
        const v = valSplit[i];
        if (!(v.includes('px'))){
          continue;
        }
        valSplit[i] = `${Number(v.replace('px', '')) * imageScale}px`;
      }
      css[key] = valSplit.join(' ');
    }
  }
  return returnObj;
};

const resolveImageResource = (resource: string, localState: GeneralState, media: Record<string, string>, allowEmptyMedia = true) => {
  const resolvedResource = fillString(resource, localState);
  if (allowEmptyMedia) {
    return media[resolvedResource] ?? resolvedResource;
  }
  return media[resolvedResource] && media[resolvedResource] !== '' ? media[resolvedResource] : resolvedResource;
};

/**
 * for elements containing a background image, calculate its size and offset
 * @param bgRes string containing the content of the background image
 * @param backgroundSize object containing the background image's dimensions
 * @param w width of the element
 * @param h height of the element
 */
const getDims = (bgRes: string, backgroundSize: AppDesign['backgroundSize'], w: number, h: number) => {
  const imWidth = backgroundSize.width;
  const imHeight = backgroundSize.height;

  // case of the background image using the border mode
  let viewPortWidth = backgroundSize.sectionWidth;
  let viewPortHeight = backgroundSize.sectionHeight;
  let posX = backgroundSize.sectionX;
  let posY = backgroundSize.sectionY;

  // if border mode is not used, revert to default values (display whole image)
  if (!backgroundSize.hasBorder) {
    viewPortWidth = imWidth;
    viewPortHeight = imHeight;
    posX = 0;
    posY = 0;
  }

  const r1 = viewPortWidth / viewPortHeight;
  const r2 = w / h;
  const imScale = r1 > r2 ? viewPortWidth / w : viewPortHeight / h;
  const scale = r1 > r2 ? imWidth / viewPortWidth : imHeight / viewPortHeight;
  const x = (w - (viewPortWidth / imScale)) / 2 - posX / imScale;
  const y = (h - (viewPortHeight / imScale)) / 2 - posY / imScale;
  return (bgRes && bgRes !== 'url()') ? `${bgRes} ${x}px ${y}px/${r1 > r2 ? `${scale * 100}% auto` : `auto ${scale * 100}%`} no-repeat` : '';
}
/**
 * rescale the background image to fit into the screen perfectly, and move it to the upper left corner
 * then, calculate the needed scale- and x/y-offset to fit the viewport requirements
 * @param bgRes string containing the content of the background image
 * @param backgroundSize object containing the background image's dimensions
 * @param w width of the element
 * @param h height of the element
 */
const getDimsBG = (bgRes: string, backgroundSize: AppDesign['backgroundSize'], w: number, h: number) => {
  const imWidth = backgroundSize.width;
  const imHeight = backgroundSize.height;

  // case of the background image using the border mode
  let viewPortWidth = backgroundSize.sectionWidth;
  let viewPortHeight = backgroundSize.sectionHeight;
  let posX = backgroundSize.sectionX;
  let posY = backgroundSize.sectionY;

  // if border mode is not used, revert to default values (display whole image)
  if (!backgroundSize.hasBorder) {
    viewPortWidth = imWidth;
    viewPortHeight = imHeight;
    posX = 0;
    posY = 0;
  }

  const r1 = viewPortWidth / viewPortHeight;
  const r2 = w / h;
  const imScaleX = imWidth / w;
  const imScaleY = imHeight / h;
  const imScale = r1 > r2 ? imScaleX : imScaleY;
  let scaleX = imWidth / viewPortWidth;
  let scaleY = imHeight / viewPortHeight;
  let scale = r1 > r2 ? scaleX : scaleY;
  // different background modes require individual x- and y-scaling
  switch (backgroundSize.scaleMode) {
    case 'contain': scaleX = scale; scaleY = scale; break;
    case 'cover': scale *= (r1 < r2 ? r2 / r1 : r1 / r2); scaleX = scale; scaleY = scale; break;
    case 'fit-width': scale *= (r1 < r2 ? r2 / r1 : 1); scaleX = scale; scaleY = scale; break;
    case 'fit-height': scale *= (r1 < r2 ? 1 : r1 / r2); scaleX = scale; scaleY = scale; break;
    case 'stretch': scaleX = scale * (r1 < r2 ? (r2 / r1) : 1) ; scaleY = scale * (r1 < r2 ?  1 : (r1 / r2)); break;
    default: scaleX = scale; scaleY = scale; break;
  }


  const x = (w / 2 - (posX + viewPortWidth / 2) / imScale);
  const y = (h / 2 - (posY + viewPortHeight / 2) / imScale);
  return {
    bgString: (bgRes && bgRes !== 'url()') ? `${bgRes} 0 0 /  ${r1 > r2 ? `${100}vw auto` : `auto ${100}vh`} no-repeat` : '',
    scale,
    scaleX,
    scaleY,
    x: x * scaleX,
    y: y * scaleY
  };
};

/**
 * create a page config with all elements that are to be displayed on the page,
 * as well as fitting the background onto the screen properly
 * @param stageName name of the current stage(page)
 * @param localState value store containing all values created and used by the application
 */
export const genElements = (stageName?: string, localState?: GeneralState, media: Record<string, string> = environment.media) => {
  const settings = environment.appDesignSettings ?? {};
  if (!stageName) {
    stageName = settings?.homeStage ?? '';
  }
  const config: Record<string, AppConfig> = _.cloneDeep(environment.appDesignConfigs);
  if (!config) {
    return {elements: undefined, pageConfig: undefined};
  }
  let page: AppConfig =  config[stageName];
  if (!page) {
    return {elements: undefined, pageConfig: undefined};
  }
  page = fillConfig(page, environment.appDesignTemplates ?? {}, environment.appDesignTemplatesElements ?? {});
  const elements = { ...page.design.elements } ?? {};
  const imageWidth = page.design.backgroundSize?.hasBorder ? page.design.backgroundSize?.sectionWidth : page?.design.backgroundSize?.width;
  const imageHeight = page.design.backgroundSize?.hasBorder ? page.design.backgroundSize?.sectionHeight : page?.design.backgroundSize?.height;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const screenRatio = screenWidth / screenHeight;

  // if the current screen's ratio is outside of the page's permitted bounds,
  // call this method with the alternative screen instead
  if (page?.design.backgroundBounds.goTo) {
    const bounds = page?.design.backgroundBounds;
    const max = bounds.maxWidth / bounds.maxHeight;
    const min = bounds.minWidth / bounds.minHeight;
    if (screenRatio > max || screenRatio < min) {
      return genElements(bounds.goTo);
    }
  }

  for (const [_i, e] of Object.entries(elements)) {
    if (e.ignoreAdjust) {
      continue;
    }

    // calculate the new dimensions of the element, according to the page's canvas as well as the device's display resolution
    const {width, height, left, top, imageScale} =
      fixPositionPx(imageWidth, imageHeight, screenWidth, screenHeight, e.css, e.css.width, e.css.height, e.css.left, e.css.top);
    if (width) {
      e.css.width = width;

    }
    if (height) {
      e.css.height = height;
    }
    if (left){
      e.css.left = left;
    }
    if (top) {
      e.css.top = top;
    }

    if (!e.css['z-index']) {
      e.css['z-index'] = '10';
    }

    if (e.maintitle && e.maintitle !== '') {
      e.maintitle = fillString(e.maintitle, localState);
    }

    if (e.elementType === 'valueMappedColor' && e.valueSource && e.colorMapping) {
      const valuePath = [e.valueSource.group, e.valueSource.key].filter(Boolean).join('.');
      const sourceValue = getKeyVal(valuePath, localState);
      const numericValue = Number(sourceValue);
      const color = getMappedColor(numericValue, e.colorMapping);

      if (color) {
        e.css[e.colorMapping.cssProperty ?? 'background-color'] = color;
      }
    }

    if (e.design) {
      e.design = media[e.design] ?? e.design;
    }
    if (e.resource && e.elementType !== 'iframe' && e.elementType !== 'iframePost') {
      e.resource = resolveImageResource(e.resource, localState, media);
      // e.resource = `${e.resource}`;
      const backgroundType = e.elementType === 'slider' ? '100% 100%' : 'contain';
      e.css.background = `url(${e.resource}) center center/${backgroundType} no-repeat`;
      e.css.transform ??= 'translateZ(0)';
    }

    // slider elements need to be calculated independently
    if (e.elementType === 'slider'){
      getSlider(e.css.width, e.css.height, imageScale, screenWidth, screenHeight, e, localState);
    }

    if (e.elementType === 'progressBar') {
      if (!e.index) {
        e.index = 0;
      }
    } else if (e.elementType === 'options') {
      const val = getKeyVal(e.handleFunction.argument, localState);
      if (val && Array.isArray(val)) {
        e.handleFunction.argument = val.join(',');
      }
    }
    // console.log(e)

    // if the element has a background image, render and position it accordingly
    if (e.background?.image && e.background.image !== '' && e.background.image !== 'none') {
      let buttonWidth;
      let buttonHeight;
      if (e.css.width.includes('px')) {
        buttonWidth = Number(e.css.width.replace('px', '') ?? '');
      } else if (e.css.width.includes('%')) {
        buttonWidth = Number(e.css.width.replace('%', '') ?? '') / 100 * screenWidth;
      } else if (e.css.width.includes('vw')) {
        buttonWidth = Number(e.css.width.replace('vw', '') ?? '') / 100 * screenWidth;
      } else if (e.css.width.includes('vh')) {
        buttonWidth = Number(e.css.width.replace('vh', '') ?? '') / 100 * screenHeight;
      }

      if (e.css.height.includes('px')) {
        buttonHeight = Number(e.css.height.replace('px', '') ?? '');
      } else if (e.css.height.includes('%')) {
        buttonHeight = Number(e.css.height.replace('%', '') ?? '') / 100 * screenHeight;
      } else if (e.css.height.includes('vh')) {
        buttonHeight = Number(e.css.height.replace('vh', '') ?? '') / 100 * screenHeight;
      } else if (e.css.height.includes('vw')) {
        buttonHeight = Number(e.css.height.replace('vw', '') ?? '') / 100 * screenWidth;
      }


      if (buttonWidth && buttonHeight) {
        const bg = resolveImageResource(e.background.image, localState, media, false);
        const bgRes = `url(${bg})`;
        e.css.background = getDims(bgRes, e.background, buttonWidth, buttonHeight);
      } else {
        const bg = resolveImageResource(e.background.image, localState, media, false);
        e.css.background = bg && bg !== '' ? `url(${bg})` : '';
      }
    }

    // iframes may have variables in their url, and they need to be filled in
    if (e.elementType === 'iframe' || e.elementType === 'iframePost') {
      let url = e.design;
      url = fillURL(url, localState);
      e.design = url;
    }

    if (e.elementType === 'link') {
      const url = fillURL(e.url, localState);
      e.url = url;
    }

    // if the element should be animated, set its call accordingly
    switch (e.move) {
      case 'fromLeft': e.class = 'move_from_left'; break;
      case 'fromRight': e.class = 'move_from_right'; break;
      case 'fromTop': e.class = 'move_from_top'; break;
      case 'fromBottom': e.class = 'move_from_bottom'; break;
      default: e.class = '';
    }
    e.class = e.class + ' shrink';
    if (stageName === settings?.homeStage && e.elementType === 'video') {
      delete elements[_i];
    }
  }
  const pageConfig = {};

  // if the page has a background image, display and scale it according to the canvas and the screen
  let bgRes = page.design?.background ?? '';
  if (page.design?.backgroundType !== 'iframe') {
    if (bgRes && bgRes !== '') {
      if (bgRes.startsWith('#')) {
        pageConfig['background'] = bgRes;
      } else {
        bgRes = `url(${resolveImageResource(bgRes, localState, media, false)})`;
        const {bgString, scale, scaleX, scaleY , x, y} = getDimsBG(bgRes, page.design.backgroundSize, screenWidth, screenHeight);
        pageConfig['background'] = bgString;
        pageConfig['scale'] = scale;
        pageConfig['scaleX'] = scaleX;
        pageConfig['scaleY'] = scaleY;
        pageConfig['x'] = x;
        pageConfig['y'] = y;
        pageConfig['--background'] = 'no-repeat';
      }
    }
  } else {
    // if the background's type is an iframe, render it accordingly
    elements.backgroundIframeCalculate = {
      background: undefined,
      css: {
        position: 'fixed',
        width: '100%',
        height: '100%',
        left: '0px',
        top: '0px',
        border: '0'
      },
      design: bgRes,
      elementType: 'iframe',
      goTo: '',
      ignoreAdjust: false,
      resource: '',
      subtitle: '',
      maintitle: 'BG'

    };
  }

  pageConfig['heading'] = page?.design.heading ?? '';
  return { elements, pageConfig };
};
